import { Progress } from "api/types";
import { IcbtFeedbackTaskCell } from "components/patients/patientCard/icbt/tablecells/IcbtFeedbackTaskCell";
import { PatientNotesCellText } from "components/patients/patientCard/icbt/tablecells/PatientNotesCellText";
import { styled } from "styled-components";
import { ProgramsAndTreatments } from "../../../hooks/icbt/useGetAllProgramsAndTreatmentsQuery";
import { IcbtTasksWithPatientData } from "../hooks/useSortedIcbtTodoTasks";
import { FeedbackDateText } from "./FeedbackDateText";
import { PatientName, PatientNameProps } from "./PatientName";
import { ProgramItem } from "./ProgramItem";
import { type QuestionnaireCounts } from "hooks/icbt/useGetQuestionnaireCountsPerProgramQuery";
import { UserAnswer } from "core/types/index";
import { ResponseDateText } from "./ResponseDateText";

export type UserAnswersByRequestIdByUserId = Record<
  string,
  Record<string, UserAnswer[]>
>;
const ItemContainer = styled.div`
  all: unset;
  margin-left: 32px;
  padding: 10px;
  border-bottom: 1px solid #e0e0e0;
  & > * {
    flex: 1;
  }
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.COLORS.RIPPLE};
  }
`;
const Clickable = styled.button`
  all: unset;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  & > * {
    flex: 1 1 20%;
  }
`;

type FeedbackItemPatientData = PatientNameProps & {
  userId: number;
};

export interface HandlerProps {
  icbtTasks: IcbtTasksWithPatientData<FeedbackItemPatientData>;
  program?: ProgramsAndTreatments["programs"][0];
  treatment?: ProgramsAndTreatments["treatments"][0];
}

interface Props {
  displayDropdownUpwards?: boolean;
  item: IcbtTasksWithPatientData<FeedbackItemPatientData>;
  progress?: Progress;
  programsAndTreatments?: ProgramsAndTreatments;
  questionnaireCounts: QuestionnaireCounts;
  userAnswersByRequestIdByUserId: UserAnswersByRequestIdByUserId;
  done?: boolean;
  withAssessments?: boolean;
  onClick: (item: HandlerProps) => void;
  editFeedbackDateFn?: () => void;
}

export const TaskItem = ({
  displayDropdownUpwards,
  item,
  progress,
  programsAndTreatments,
  onClick,
  editFeedbackDateFn,
  questionnaireCounts,
  userAnswersByRequestIdByUserId,
  done = false,
}: Props) => {
  const program = programsAndTreatments?.programs[item.programId];
  const treatment = programsAndTreatments?.treatments[item.treatmentId];
  const totalQuestionnairesInProgram =
    questionnaireCounts[program?.id ?? ""] ?? 0;

  const questionnairesInProgramAnswered =
    userAnswersByRequestIdByUserId[item.patientUserId]?.[item.icbtRequestId]
      ?.length ?? 0;
  const hasFeedbackAndAssessment = !!(
    item.feedbackPublishedAt &&
    item.assessmentStatus.status !== "AWAITING_ASSESSMENT"
  );

  const responseDate = item.patientResponsePublishedAt
    ? new Date(item.patientResponsePublishedAt)
    : false;
  const responseIsNewerThanFeedback =
    responseDate && new Date(item.feedbackPublishedAt ?? 0) < responseDate;

  return (
    <ItemContainer>
      <Clickable
        onClick={(e) => {
          e.preventDefault();
          onClick({ icbtTasks: item, program, treatment });
        }}
      >
        <ResponseDateText
          responseIsNewerThanFeedback={responseIsNewerThanFeedback}
          date={responseDate}
        />

        <PatientName patient={item.patient} />
        <ProgramItem
          program={program}
          treatment={treatment}
          progress={progress}
        />
        <PatientNotesCellText
          questionnairesInProgramAnswered={questionnairesInProgramAnswered}
          totalQuestionnairesInProgram={totalQuestionnairesInProgram}
          patientNoteCount={item.patientResponseCount ?? 0}
        />

        <IcbtFeedbackTaskCell
          displayDropdownUpwards={displayDropdownUpwards}
          tasks={item}
          editFeedbackDateFn={editFeedbackDateFn}
        />

        <FeedbackDateText
          feedbackDate={item.feedbackDate}
          hasFeedback={hasFeedbackAndAssessment}
          done={done}
        />
      </Clickable>
    </ItemContainer>
  );
};
